import { Alert, flash } from "@xola/ui-kit";
import ObjectId from "bson-objectid";
import { FormField, InputField, requiredString } from "common";
import { FormikProps } from "formik";
import pluralize from "pluralize";
import React, { forwardRef, Ref, useState } from "react";
import { useMutation } from "react-query";
import { EmvDevice, Permission } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useCollection } from "../../../hooks/useCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { Loader } from "../../Loader";
import { AddDeviceModal } from "./AddDeviceModal";
import { Device } from "./Device";

const validationSchema = object().shape({
    label: requiredString(),
});

interface PaymentTerminalValues {
    id: ID;
    label: string;
}

export type PaymentTerminalFormRef = FormikProps<PaymentTerminalValues>;

export const PaymentTerminalForm = forwardRef((props, ref: Ref<PaymentTerminalFormRef>) => {
    const seller = useSeller();
    const { isGranted } = useAuthorization();

    const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false);
    const computer = localStorage.getItem("computer"); // TODO: This key is used in V1 but it's too generic, local storage will be refactored in upcoming ticket
    const data: PaymentTerminalValues = computer ? JSON.parse(computer ?? "") : { id: "", label: "" };
    const [, setPosLabel] = useState<{ id: ID | ObjectId; label: string }>(data); // Quick cheat to re-render form when local storage is updated

    const invalidate = useInvalidate();
    const { data: devices, isLoading } = useCollection<EmvDevice>([
        "users",
        seller.id,
        "preferences",
        "stripeTerminal",
        "paymentDevices",
    ]);
    const [editDevice, setEditDevice] = useState<EmvDevice | null>(null);

    const { mutateAsync: deleteDevice } = useMutation(async (device: EmvDevice) => {
        return api.delete(`/users/${seller.id}/preferences/stripeTerminal/paymentDevices/${device.id}`);
    });

    const toggleAddDeviceModal = () => {
        if (!isAddDeviceOpen && !computer) {
            return flash.show({
                text: "Please set a POS Station label & click 'Save' below before adding a device",
                color: "danger",
            });
        }

        setIsAddDeviceOpen(!isAddDeviceOpen);
    };

    const handlePOSLabelSubmit = (values: { label: string }) => {
        const id = data.id || new ObjectId(); // Create a new object ID if current is empty
        const payload = { id, label: values.label.trim() };
        localStorage.setItem("computer", JSON.stringify(payload));
        setPosLabel(payload); // Get form to re-render
        flash.success("POS Label saved");
    };

    const handleDelete = async (device: EmvDevice) => {
        await deleteDevice(device);
        invalidate("sellers", seller.id);
        invalidate("users", seller.id);
        invalidate("paymentDevices");
    };

    const handleEdit = (device: EmvDevice) => {
        setEditDevice(device);
        toggleAddDeviceModal();
    };

    const closeDeviceModal = () => {
        setEditDevice(null);
        toggleAddDeviceModal();
    };

    const canEditOrPairPaymentDevice = isGranted(Permission.EDIT_PAIR_PAYMENT_DEVICE);

    const pairedDevices = devices.filter((device) => device.computer?.id === data.id);
    const unpairedDevices = devices.filter((device) => device.computer?.id !== data.id);

    const posLabel = data.label ? data.label : "Not set by your admin, please contact your admin to set the POS label.";

    const pairDeviceLabel =
        pairedDevices.length > 0 && data.label ? (
            <div>Pair the registered Verifone Devices with your POS system.</div>
        ) : (
            <div>Device can be paired only once this label is set.</div>
        );

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <XolaFormik
                enableReinitialize
                innerRef={ref}
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={data}
                onSubmit={handlePOSLabelSubmit}
            >
                {() => (
                    <div className="space-y-8">
                        <div>
                            {canEditOrPairPaymentDevice ? (
                                <FormField
                                    name="label"
                                    label="POS Station Label"
                                    tooltip="Label to uniquely identify this POS"
                                >
                                    <InputField placeholder="Enter a label to identify this POS" />
                                </FormField>
                            ) : (
                                canEditOrPairPaymentDevice && (
                                    <FormField
                                        name="label"
                                        label="POS Station Label"
                                        tooltip="Label to uniquely identify this POS"
                                    >
                                        <NoTranslate>
                                            {posLabel} {pairDeviceLabel}
                                        </NoTranslate>
                                    </FormField>
                                )
                            )}
                        </div>

                        <div className="space-y-2">
                            {(pairedDevices.length > 0 || unpairedDevices.length > 0) && (
                                <>
                                    <div className="text-sm font-bold">
                                        Paired {pluralize("Device", unpairedDevices.length)}
                                    </div>
                                    <Devices devices={pairedDevices} onDelete={handleDelete} onEdit={handleEdit} />
                                    {pairedDevices.length === 0 && (
                                        <Alert>
                                            Please pair a device from the list of available devices below to start
                                            accepting EMV payments.
                                        </Alert>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="space-y-2">
                            <div className="text-sm font-bold">Available Devices</div>
                            <Devices devices={unpairedDevices} onDelete={handleDelete} onEdit={handleEdit} />
                            {unpairedDevices.length === 0 && <p className="text-sm">No devices available</p>}
                            {canEditOrPairPaymentDevice && (
                                <SecondaryButton size="small" onClick={toggleAddDeviceModal}>
                                    Add Device
                                </SecondaryButton>
                            )}
                        </div>
                    </div>
                )}
            </XolaFormik>
            <AddDeviceModal isOpen={isAddDeviceOpen} device={editDevice ?? undefined} onClose={closeDeviceModal} />
        </>
    );
});

interface DevicesInterface {
    devices: EmvDevice[];
    canEdit?: boolean;
    onDelete?: (device: EmvDevice) => void;
    onEdit?: (device: EmvDevice) => void;
}

export const Devices = ({ devices, canEdit = true, onDelete, onEdit }: DevicesInterface) => {
    return (
        <div className="space-y-2">
            {devices.map((device) => {
                return (
                    <Device
                        key={device.id ?? device.remoteId}
                        canEdit={canEdit}
                        device={device}
                        onDelete={onDelete}
                        onEdit={onEdit}
                    />
                );
            })}
        </div>
    );
};
